
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import * as Sentry from "@sentry/react";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Tournament from "./pages/Tournament";
import Leaderboard from "./pages/Leaderboard";
import League from "./pages/League";
import LeagueOverview from "./pages/LeagueOverview";
import CreateLeague from "./pages/CreateLeague";
import Profile from "./pages/Profile";
import XPLedgerPage from "./pages/XPLedgerPage";
import Diagnostics from "./pages/Diagnostics";
import BuyFieldPass from "./pages/BuyFieldPass";
import Rewards from "./pages/Rewards";
import SetLineup from "./pages/SetLineup";
import DraftRoom from "./pages/DraftRoom";
import Team from "./pages/Team";
import MatchCenter from "./pages/MatchCenter";
import Matches from "./pages/Matches";
import Notifications from "./pages/Notifications";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto text-primary animate-pulse mb-4">⚡</div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto text-primary animate-pulse mb-4">⚡</div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
            <div className="min-h-screen bg-background flex items-center justify-center">
              <div className="text-center p-8">
                <h1 className="text-2xl font-bold text-destructive mb-4">Something went wrong</h1>
                <p className="text-muted-foreground mb-4">{error instanceof Error ? error.message : 'An unexpected error occurred'}</p>
                <button 
                  onClick={resetError}
                  className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
                >
                  Try again
                </button>
              </div>
            </div>
          )}>
            <Routes>
              <Route 
                path="/" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/auth" 
                element={
                  <PublicRoute>
                    <Auth />
                  </PublicRoute>
                } 
              />
              <Route 
                path="/leaderboard" 
                element={
                  <ProtectedRoute>
                    <Leaderboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/leagues" 
                element={
                  <ProtectedRoute>
                    <League />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/league/:id" 
                element={
                  <ProtectedRoute>
                    <LeagueOverview />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/leagues/create" 
                element={
                  <ProtectedRoute>
                    <CreateLeague />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/xp-ledger" 
                element={
                  <ProtectedRoute>
                    <XPLedgerPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/diagnostics" 
                element={
                  <ProtectedRoute>
                    <Diagnostics />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/buy-field-pass" 
                element={
                  <ProtectedRoute>
                    <BuyFieldPass />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/rewards" 
                element={
                  <ProtectedRoute>
                    <Rewards />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/league/:leagueId/player/:playerId/gw/:gwNumber" 
                element={
                  <ProtectedRoute>
                    <SetLineup />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/league/:id/draft-room" 
                element={
                  <ProtectedRoute>
                    <DraftRoom />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/team" 
                element={
                  <ProtectedRoute>
                    <Team />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/matches" 
                element={
                  <ProtectedRoute>
                    <Matches />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/match-center" 
                element={
                  <ProtectedRoute>
                    <MatchCenter />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/notifications" 
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                } 
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Sentry.ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
